/**
 * AI Layout Service
 * Handles communication with backend AI layout generation endpoints
 */

import { Component, ResponsivePosition } from '@/types';
import { ResponsiveLayoutAnalyzer, ResponsiveLayoutResult as FrontendLayoutResult, FlexContainerConfig } from './ResponsiveLayoutAnalyzer';
import { FlexContainerWrapper } from '@/utils/flexContainerWrapper';
import { config } from '@/config';
import { getCurrentAccessToken } from '@/services/api/auth-session';
import axios from 'axios';

const API_BASE_URL = config.api.baseUrl;

interface LayoutPosition {
  x: number;
  y: number;
  z: number;
}

interface LayoutBox extends LayoutPosition {
  width: number;
  height: number;
}

export interface AILayoutResponse {
  success: boolean;
  data: {
    containers: FlexContainerConfig[];
    componentUpdates: Array<{
      componentId: string;
      responsivePosition: ResponsivePosition;
      parentContainerId?: string;
    }>;
    layoutScore: number;
    recommendations: string[];
  };
}

export class AILayoutService {
  private static instance: AILayoutService;
  private analyzer: ResponsiveLayoutAnalyzer;

  private constructor() {
    this.analyzer = new ResponsiveLayoutAnalyzer();
  }

  private getAuthHeaders(): Record<string, string> {
    const token = getCurrentAccessToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  }

  private toNumericDimension(value: string | number | undefined, fallback = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    return fallback;
  }

  private getLayoutPosition(component: Component): LayoutPosition {
    const responsivePosition = component.responsivePosition;
    const desktopPosition = responsivePosition?.breakpoints?.desktop?.position;
    const percentagePosition = responsivePosition?.constraints?.percentagePosition;

    return {
      x: this.toNumericDimension(desktopPosition?.x ?? percentagePosition?.x),
      y: this.toNumericDimension(desktopPosition?.y ?? percentagePosition?.y),
      z: this.toNumericDimension(desktopPosition?.z ?? component.zIndex),
    };
  }

  private getLayoutBox(component: Component): LayoutBox {
    const position = this.getLayoutPosition(component);
    const responsiveSize = component.responsivePosition?.responsiveSize;
    const desktopSize = component.responsivePosition?.breakpoints?.desktop?.size;

    return {
      ...position,
      width: this.toNumericDimension(
        desktopSize?.width ?? responsiveSize?.width ?? component.size.width
      ),
      height: this.toNumericDimension(
        desktopSize?.height ?? responsiveSize?.height ?? component.size.height
      ),
    };
  }

  public static getInstance(): AILayoutService {
    if (!AILayoutService.instance) {
      AILayoutService.instance = new AILayoutService();
    }
    return AILayoutService.instance;
  }

  /**
   * Generate responsive layout locally (frontend-only)
   */
  public async generateResponsiveLayoutLocal(
    components: Component[],
    canvasWidth: number = 1200,
    canvasHeight: number = 800
  ): Promise<FrontendLayoutResult> {
    console.log('🎨 Generating responsive layout locally', {
      componentCount: components.length,
      canvasSize: { width: canvasWidth, height: canvasHeight }
    });

    const result = this.analyzer.analyzeLayout(components, canvasWidth, canvasHeight);

    console.log('✅ Local responsive layout generated', {
      containerCount: result.rootContainers.length,
      layoutScore: result.layoutScore,
      recommendations: result.recommendations
    });

    return result;
  }

  /**
   * Generate responsive layout using backend API
   */
  public async generateResponsiveLayoutAPI(
    components: Component[],
    canvasWidth: number = 1200,
    canvasHeight: number = 800
  ): Promise<AILayoutResponse> {
    try {
      console.log('🌐 Calling backend AI layout generation', {
        componentCount: components.length,
        canvasSize: { width: canvasWidth, height: canvasHeight }
      });

      // Prepare components for API
      const apiComponents = components.map(c => ({
        id: c.id,
        name: c.name,
        type: c.type,
        position: this.getLayoutPosition(c),
        size: c.size
      }));

      const response = await axios.post<AILayoutResponse>(
        `${API_BASE_URL}/ai/layout/generate-responsive`,
        {
          components: apiComponents,
          canvasWidth,
          canvasHeight
        },
        {
          headers: this.getAuthHeaders()
        }
      );

      console.log('✅ Backend responsive layout generated', {
        success: response.data.success,
        containerCount: response.data.data?.containers?.length,
        layoutScore: response.data.data?.layoutScore
      });

      return response.data;

    } catch (error) {
      console.error('❌ Failed to generate layout via API:', error);

      // Fallback to local generation
      console.log('🔄 Falling back to local generation');
      const localResult = await this.generateResponsiveLayoutLocal(components, canvasWidth, canvasHeight);

      // Convert local result to API format
      return {
        success: true,
        data: {
          containers: localResult.rootContainers,
          componentUpdates: Array.from(localResult.componentUpdates.entries()).map(([id, update]) => ({
            componentId: id,
            responsivePosition: update.responsivePosition,
            parentContainerId: update.parentContainerId
          })),
          layoutScore: localResult.layoutScore,
          recommendations: localResult.recommendations
        }
      };
    }
  }

  /**
   * Apply responsive layout to components
   */
  public applyResponsiveLayout(
    components: Component[],
    layoutResult: AILayoutResponse['data']
  ): Component[] {
    console.log('🔧 Applying responsive layout to components', {
      componentCount: components.length,
      containerCount: layoutResult.containers.length,
      updateCount: layoutResult.componentUpdates.length
    });

    // Create a map for quick lookup
    const componentMap = new Map<string, Component>();
    components.forEach(c => componentMap.set(c.id, c));

    // Create component updates map
    const updateMap = new Map<string, {
      responsivePosition: ResponsivePosition;
      parentContainerId?: string;
    }>();
    layoutResult.componentUpdates.forEach(update => {
      updateMap.set(update.componentId, {
        responsivePosition: update.responsivePosition,
        parentContainerId: update.parentContainerId
      });
    });

    // Use FlexContainerWrapper to wrap components
    const wrapperResult = FlexContainerWrapper.wrapComponents(
      components,
      layoutResult.containers,
      updateMap
    );

    console.log('✅ Responsive layout applied', {
      wrappedComponentCount: wrapperResult.wrappedComponents.length,
      rootContainerCount: wrapperResult.rootContainers.length,
      hierarchySize: wrapperResult.containerHierarchy.size
    });

    // Build a complete component list with proper hierarchy
    // First add all containers with their project/page IDs
    const currentProjectId = components[0]?.projectId || '';
    const currentPageId = components[0]?.pageId || '';

    const containersWithIds = wrapperResult.rootContainers.map(container => ({
      ...container,
      projectId: currentProjectId,
      pageId: currentPageId
    }));

    // Then add wrapped components with proper IDs
    const wrappedWithIds = wrapperResult.wrappedComponents.map(comp => ({
      ...comp,
      projectId: currentProjectId,
      pageId: currentPageId
    }));

    // Combine all components - containers and wrapped components
    const result = [
      ...containersWithIds,
      ...wrappedWithIds
    ];

    return result;
  }

  /**
   * Parse AI response for layout instructions
   */
  public async parseLayoutFromAI(aiResponse: string): Promise<unknown> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/ai/layout/parse-layout`,
        { aiResponse },
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to parse layout from AI:', error);
      return null;
    }
  }

  /**
   * Apply layout actions to components
   */
  public async applyLayoutActions(
    instructions: Array<Record<string, unknown>>,
    components: Component[]
  ): Promise<unknown> {
    try {
      const apiComponents = components.map(c => ({
        id: c.id,
        name: c.name,
        type: c.type,
        position: this.getLayoutPosition(c),
        size: c.size
      }));

      const response = await axios.post(
        `${API_BASE_URL}/ai/layout/apply-layout`,
        {
          instructions,
          components: apiComponents
        },
        {
          headers: this.getAuthHeaders()
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to apply layout actions:', error);
      return null;
    }
  }

  /**
   * Generate layout recommendations
   */
  public generateRecommendations(
    components: Component[],
    layoutScore: number
  ): string[] {
    const recommendations: string[] = [];

    if (layoutScore < 70) {
      recommendations.push('Consider reorganizing components for better visual hierarchy');
    }

    if (components.length > 10) {
      recommendations.push('Group related components using containers for better organization');
    }

    const largeComponents = components.filter(c => this.getLayoutBox(c).width > 600);
    if (largeComponents.length > 0) {
      recommendations.push(`${largeComponents.length} component(s) may be too wide for mobile devices`);
    }

    // Check for overlapping components
    const overlaps = this.detectOverlaps(components);
    if (overlaps > 0) {
      recommendations.push(`${overlaps} overlapping component(s) detected - consider adjusting positions`);
    }

    return recommendations;
  }

  /**
   * Detect overlapping components
   */
  private detectOverlaps(components: Component[]): number {
    let overlaps = 0;

    for (let i = 0; i < components.length - 1; i++) {
      for (let j = i + 1; j < components.length; j++) {
        const c1 = components[i];
        const c2 = components[j];
        const box1 = this.getLayoutBox(c1);
        const box2 = this.getLayoutBox(c2);

        // Check for overlap
        if (box1.x < box2.x + box2.width &&
            box1.x + box1.width > box2.x &&
            box1.y < box2.y + box2.height &&
            box1.y + box1.height > box2.y) {
          overlaps++;
        }
      }
    }

    return overlaps;
  }
}
